/**
 * Series Detail — shows series books + Story Memory / character bible
 * F-12 per UX mockup
 */
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { ApiSuccess } from '../lib/api';
import { Navbar } from '../components/layout/Navbar';
import { useBooks } from '../hooks/useBooks';

interface SeriesBook {
  id: string;
  title: string;
  wordCount: number;
  status: string;
}

interface SeriesCharacter {
  id: string;
  name: string;
  role: string;
  physicalDescription: string;
  personalityNotes: string;
  firstAppearedBookId: string;
  status: 'active' | 'deceased' | 'unknown';
}

interface WorldDetail {
  id: string;
  category: 'location' | 'technology' | 'magic' | 'politics' | 'culture' | 'other';
  name: string;
  description: string;
}

interface SeriesDetailResponse {
  id: string;
  title: string;
  genre?: string;
  books: SeriesBook[];
  characters?: SeriesCharacter[];
  worldDetails?: WorldDetail[];
  timelineNotes?: string;
}

export function SeriesDetail() {
  const { seriesId } = useParams<{ seriesId: string }>();
  const qc = useQueryClient();
  const [showLink, setShowLink] = useState(false);
  const [selectedBookId, setSelectedBookId] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);

  const { data: seriesData, isLoading } = useQuery({
    queryKey: ['series', seriesId],
    queryFn: async () => {
      const res = await api.get<ApiSuccess<SeriesDetailResponse>>(`/series/${seriesId}`);
      return res.data.data ?? null;
    },
    enabled: !!seriesId,
  });

  const { data: allBooks } = useBooks();

  const linkMutation = useMutation({
    mutationFn: async (bookId: string) => {
      await api.post(`/series/${seriesId}/books`, { bookId });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['series', seriesId] });
      void qc.invalidateQueries({ queryKey: ['series'] });
      setShowLink(false);
      setSelectedBookId('');
      setLinkError(null);
    },
    onError: (err: { response?: { data?: { error?: { message?: string } } } }) => {
      setLinkError(err.response?.data?.error?.message ?? 'Failed to link book. Please try again.');
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!seriesData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Series not found</p>
          <Link to="/series" className="text-primary hover:underline">← Back to Series</Link>
        </div>
      </div>
    );
  }

  const books = seriesData.books ?? [];

  // Derive character bible data from seriesData (already returned by GET /series/:id)
  const characters = seriesData.characters ?? [];
  const worldDetails = seriesData.worldDetails ?? [];
  const hasBible = characters.length > 0 || worldDetails.length > 0;

  // Split worldDetails into locations and world rules
  const locations = worldDetails
    .filter((w) => w.category === 'location')
    .map((w) => w.name);
  const worldRules = worldDetails
    .filter((w) => w.category !== 'location')
    .map((w) => `${w.name}: ${w.description}`);

  return (
    <div className="min-h-screen bg-background">
      <Navbar variant="authed" />

      <main className="px-6 py-8 max-w-4xl mx-auto w-full">
          <div className="flex items-center gap-4 mb-6">
            <Link to="/series" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              ← Series
            </Link>
            <h1 className="text-2xl font-bold text-foreground">{seriesData.title}</h1>
            {seriesData.genre && (
              <span className="px-2 py-0.5 bg-primary/15 text-primary/80 rounded-full text-xs font-medium">{seriesData.genre}</span>
            )}
          </div>

          {/* Empty state — no books yet */}
          {books.length === 0 && !showLink && (
            <div className="flex flex-col sm:flex-row gap-4 justify-center py-8 mb-6">
              <Link
                to={`/books/new?seriesId=${seriesId}`}
                className="flex-1 max-w-xs p-6 border border-border rounded-xl bg-card hover:border-primary/50 hover:bg-accent/30 transition-colors text-center group"
              >
                <div className="text-3xl mb-3">✍️</div>
                <h3 className="font-semibold text-foreground mb-1">Create a new book</h3>
                <p className="text-xs text-muted-foreground">Start writing the first book in this series from scratch.</p>
              </Link>
              <button
                onClick={() => setShowLink(true)}
                className="flex-1 max-w-xs p-6 border border-border rounded-xl bg-card hover:border-primary/50 hover:bg-accent/30 transition-colors text-center group"
              >
                <div className="text-3xl mb-3">🔗</div>
                <h3 className="font-semibold text-foreground mb-1">Link an existing book</h3>
                <p className="text-xs text-muted-foreground">Add a book you've already written as the first entry.</p>
              </button>
            </div>
          )}

          {/* Link existing book panel */}
          {showLink && (
            <div className="mb-6 p-5 border border-primary/30 rounded-xl bg-card">
              <h2 className="font-semibold text-foreground mb-3">Link an existing book</h2>
              <select
                value={selectedBookId}
                onChange={(e) => setSelectedBookId(e.target.value)}
                className="w-full px-3 py-2 bg-input border border-border rounded-md text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring mb-3"
              >
                <option value="">— Select a book —</option>
                {(allBooks ?? []).map((b) => (
                  <option key={b.id} value={b.id}>{b.title}</option>
                ))}
              </select>
              {linkError && <p className="text-sm text-destructive mb-3">{linkError}</p>}
              <div className="flex gap-3">
                <button
                  onClick={() => { if (selectedBookId) linkMutation.mutate(selectedBookId); }}
                  disabled={!selectedBookId || linkMutation.isPending}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  {linkMutation.isPending ? 'Linking…' : 'Link Book'}
                </button>
                <button
                  onClick={() => { setShowLink(false); setSelectedBookId(''); setLinkError(null); }}
                  className="px-4 py-2 border border-border text-foreground rounded-md hover:bg-accent transition-colors text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Books in series */}
          {books.length > 0 && (
            <>
              <div className="space-y-3 mb-6">
                {books.map((book, idx) => (
                  <div key={book.id} className="flex items-center gap-4 p-4 border border-border rounded-lg bg-card">
                    <div className="w-9 h-14 rounded bg-primary/20 flex items-center justify-center text-sm font-bold text-primary shrink-0">
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-foreground text-sm">{book.title}</div>
                      <div className="text-xs text-muted-foreground">{(book.wordCount ?? 0).toLocaleString()} words · {book.status}</div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      book.status === 'complete' ? 'bg-green-900/30 text-green-400' : 'bg-yellow-900/30 text-yellow-400'
                    }`}>
                      {book.status === 'complete' ? 'Done' : 'In Progress'}
                    </span>
                    <Link
                      to={`/books/${book.id}`}
                      className="text-sm text-muted-foreground border border-border rounded-md px-3 py-1 hover:bg-accent transition-colors"
                    >
                      Open
                    </Link>
                  </div>
                ))}
              </div>

              <div className="flex justify-center mb-8">
                <Link
                  to={`/books/new?seriesId=${seriesId}`}
                  className="max-w-xs w-full p-6 border border-border rounded-xl bg-card hover:border-primary/50 hover:bg-accent/30 transition-colors text-center"
                >
                  <div className="text-3xl mb-3">✍️</div>
                  <h3 className="font-semibold text-foreground mb-1">Write Book {books.length + 1}</h3>
                  <p className="text-xs text-muted-foreground">Continue the series with Story Memory already loaded.</p>
                </Link>
              </div>
            </>
          )}

          {/* Story Memory / Character Bible — read from series document (no separate endpoint needed) */}
          {hasBible && (
            <div className="p-5 border border-primary/20 rounded-xl bg-primary/5">
              <h2 className="text-sm font-bold text-primary mb-4">📖 Story Memory — shared context for all sequels</h2>

              {characters.length > 0 && (
                <div className="mb-5">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">Characters</h3>
                  <div className="space-y-2">
                    {characters.map((char, i) => (
                      <div key={char.id ?? i} className="p-3 bg-white/5 border border-border rounded-md">
                        <div className="text-sm font-bold text-foreground">{char.name}</div>
                        <div className="text-xs text-muted-foreground leading-relaxed mt-1">
                          {[char.role, char.personalityNotes].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {worldRules.length > 0 && (
                <div className="mb-5">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">World Rules</h3>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    {worldRules.map((rule, i) => (
                      <li key={i}>• {rule}</li>
                    ))}
                  </ul>
                </div>
              )}

              {locations.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">Locations</h3>
                  <div className="flex flex-wrap gap-2">
                    {locations.map((loc, i) => (
                      <span key={i} className="text-xs px-2 py-1 bg-white/5 border border-border rounded-md text-muted-foreground">{loc}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
      </main>
    </div>
  );
}
